import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";
import AddPetModal from "@/components/AddPetModal";
import AddReminderModal from "@/components/AddReminderModal";
import { Calendar, Plus, PawPrint, AlertTriangle, Plane, Shield, CheckCircle2, Trash2, Edit2, Syringe, Scissors, Pill, Stethoscope, Clock, AlarmClock, Check, AlertCircle, X as XIcon, Sparkles, Heart, Cake, Gift, History, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REMINDER_ICONS = {
  vaccine: Syringe,
  medication: Pill,
  grooming: Scissors,
  vet_visit: Stethoscope,
  custom: Calendar,
};
const REMINDER_COLORS = {
  vaccine: { dot: "#81C784", bg: "bg-[#E8F5E9]", text: "text-[#388E3C]" },
  medication: { dot: "#FF8A65", bg: "bg-[#FFF3E0]", text: "text-[#E65100]" },
  grooming: { dot: "#FFD54F", bg: "bg-[#FFFDE7]", text: "text-[#F57F17]" },
  vet_visit: { dot: "#64B5F6", bg: "bg-[#E3F2FD]", text: "text-[#1565C0]" },
  custom: { dot: "#CE93D8", bg: "bg-[#F3E5F5]", text: "text-[#6A1B9A]" },
};
const SPECIES_LABELS = { dog: "Dog", cat: "Cat", bird: "Bird", rabbit: "Rabbit", fish: "Fish", hamster: "Hamster", other: "Other" };

function getDateStatus(dueDateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, isOverdue: true, isToday: false };
  if (diff === 0) return { label: "Due today", isOverdue: false, isToday: true };
  if (diff === 1) return { label: "Due tomorrow", isOverdue: false, isToday: false };
  return { label: `In ${diff} days`, isOverdue: false, isToday: false };
}

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function calculateAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  const years = today.getFullYear() - birth.getFullYear();
  const months = today.getMonth() - birth.getMonth();
  if (years === 0) return `${Math.max(0, months)} mo`;
  return `${years} yr${years > 1 ? "s" : ""}`;
}

// Care timeline: visual config + grouping helpers. The timeline is the
// "history is here, leaving is hard" loop — it accumulates value with use.
// NOTE: user-facing labels resolved via i18n `t()` in the render path.
const TIMELINE_KIND = {
  vaccine:     { Icon: Syringe,     bg: "bg-[#E8F5E9]", color: "text-[#388E3C]", dot: "#66BB6A", labelKey: "timeline.vaccinated" },
  medication:  { Icon: Pill,        bg: "bg-[#FFF3E0]", color: "text-[#E65100]", dot: "#FF8A65", labelKey: "timeline.medicationGiven" },
  grooming:    { Icon: Scissors,    bg: "bg-[#FFFDE7]", color: "text-[#F57F17]", dot: "#FFD54F", labelKey: "timeline.groomed" },
  vet_visit:   { Icon: Stethoscope, bg: "bg-[#E3F2FD]", color: "text-[#1565C0]", dot: "#64B5F6", labelKey: "timeline.vetVisit" },
  vet_record:  { Icon: FileText,    bg: "bg-[#EDE7F6]", color: "text-[#5E35B2]", dot: "#9575CD", labelKey: "timeline.documentAdded" },
  custom:      { Icon: CheckCircle2,bg: "bg-[#F1F5F9]", color: "text-[#475569]", dot: "#94A3B8", labelKey: "timeline.completed" },
};

function timelineGroupKey(iso) {
  if (!iso) return "Unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown";
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

// Vaccination status — colour-coded so a glance at the dashboard answers
// "do I need to do something for this pet?". Anxiety is the retention loop:
// red (overdue) and amber (due soon) jump out, green (current) reassures.
function vaccStatus(nextDue) {
  if (!nextDue) {
    return {
      tone: "unknown",
      label: "no due date",
      Icon: AlertCircle,
      // muted so an unset date doesn't visually compete with a real overdue
      iconClass: "text-slate-300",
      labelClass: "text-slate-400",
      sortRank: 2,
    };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(nextDue);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (days < 0) {
    const abs = Math.abs(days);
    return {
      tone: "overdue",
      label: `overdue ${abs}d`,
      Icon: XIcon,
      iconClass: "text-[#C62828]",
      labelClass: "text-[#C62828] font-semibold",
      sortRank: 0,
    };
  }
  if (days <= 30) {
    return {
      tone: "due-soon",
      label: days === 0 ? "due today" : `due in ${days}d`,
      Icon: AlertCircle,
      iconClass: "text-[#E65100]",
      labelClass: "text-[#E65100] font-semibold",
      sortRank: 1,
    };
  }
  if (days <= 90) {
    return {
      tone: "due-3mo",
      label: `due in ${Math.round(days / 30)} mo`,
      Icon: AlertCircle,
      iconClass: "text-[#FF8A65]",
      labelClass: "text-[#FF8A65]",
      sortRank: 1,
    };
  }
  return {
    tone: "current",
    label: "current",
    Icon: Check,
    iconClass: "text-[#388E3C]",
    labelClass: "text-[#388E3C]",
    sortRank: 3,
  };
}

// Wellness score: 0-100 per pet. Deterministic, explainable, composed from
// data the user has already entered. No AI needed. Green ≥ 85, amber 60-84,
// red < 60. Recomputes on every render — cheap and always up to date.
//
// Weighting rationale:
// - Vaccinations carry the most weight because they're the core health signal
//   and are legally required for many species (rabies in particular).
// - Reminders reflect whether the user is actively engaged with the app.
// - "Polish" factors (photo, emergency contact) are small nudges to complete
//   the profile. They matter but they don't dominate the score.
function computeWellness(pet, reminders) {
  const reasons = [];
  let score = 0;

  // Vaccinations (60 pts max) ────────────────────────────────────────────
  const vaccs = Array.isArray(pet.vaccinations) ? pet.vaccinations.filter(v => v?.name) : [];
  if (vaccs.length === 0) {
    reasons.push({ text: "Add vaccinations to start tracking health", severity: "high" });
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let overdue = 0;
    let dueSoon = 0;
    let current = 0;
    let unknownDate = 0;
    for (const v of vaccs) {
      if (!v.next_due) { unknownDate++; continue; }
      const due = new Date(v.next_due);
      due.setHours(0, 0, 0, 0);
      const days = (due - today) / (1000 * 60 * 60 * 24);
      if (days < 0) overdue++;
      else if (days <= 30) dueSoon++;
      else current++;
    }
    const vaccTotal = vaccs.length;
    // Base: fraction of current vaccinations × 60
    const vaccRatio = current / vaccTotal;
    score += Math.round(60 * vaccRatio);
    if (overdue > 0) reasons.push({ text: `${overdue} vaccination${overdue > 1 ? "s" : ""} overdue`, severity: "high" });
    if (dueSoon > 0) reasons.push({ text: `${dueSoon} vaccination${dueSoon > 1 ? "s" : ""} due this month`, severity: "medium" });
    if (unknownDate > 0) reasons.push({ text: `${unknownDate} vaccination${unknownDate > 1 ? "s" : ""} missing a next-due date`, severity: "low" });
  }

  // Reminders (20 pts max) ───────────────────────────────────────────────
  const petReminders = reminders.filter(r => r.pet_id === pet.pet_id);
  if (petReminders.length === 0) {
    // Neutral — no reminders yet isn't bad per se, just incomplete
    score += 10;
    reasons.push({ text: "No reminders set for this pet yet", severity: "low" });
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const active = petReminders.filter(r => !r.completed);
    const overdueRem = active.filter(r => {
      if (!r.due_date) return false;
      const due = new Date(r.due_date);
      due.setHours(0, 0, 0, 0);
      return due < today;
    });
    if (overdueRem.length === 0) score += 20;
    else if (overdueRem.length === 1) { score += 10; reasons.push({ text: "1 overdue reminder", severity: "high" }); }
    else { score += 0; reasons.push({ text: `${overdueRem.length} overdue reminders`, severity: "high" }); }
  }

  // Recent activity (10 pts max) ─────────────────────────────────────────
  // Latest weight log OR most recently updated_at — a proxy for "user opened
  // this pet's page recently enough that the data reflects reality".
  const lastWeightLog = Array.isArray(pet.weight_logs) && pet.weight_logs.length
    ? pet.weight_logs[pet.weight_logs.length - 1]
    : null;
  const lastActivity = lastWeightLog?.date || pet.updated_at;
  if (lastActivity) {
    const daysAgo = (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24);
    if (daysAgo <= 30) score += 10;
    else if (daysAgo <= 90) score += 5;
    else reasons.push({ text: "No recent updates — log this month's weight?", severity: "low" });
  }

  // Profile polish (10 pts max — 5 each) ─────────────────────────────────
  if (pet.photo_url) score += 5;
  else reasons.push({ text: "Add a photo", severity: "low" });
  const hasEmergency = Array.isArray(pet.emergency_contacts) && pet.emergency_contacts.length > 0;
  if (hasEmergency) score += 5;
  else reasons.push({ text: "Add an emergency contact", severity: "low" });

  score = Math.max(0, Math.min(100, score));

  let tone, label, iconBg, iconColor, barColor, textColor;
  if (score >= 85) {
    tone = "great";
    label = "Great shape";
    iconBg = "bg-[#E8F5E9]";
    iconColor = "text-[#388E3C]";
    barColor = "bg-[#66BB6A]";
    textColor = "text-[#2E7D32]";
  } else if (score >= 60) {
    tone = "ok";
    label = "A few things to check";
    iconBg = "bg-[#FFF3E0]";
    iconColor = "text-[#E65100]";
    barColor = "bg-[#FFB74D]";
    textColor = "text-[#E65100]";
  } else {
    tone = "attention";
    label = "Needs attention";
    iconBg = "bg-[#FFEBEE]";
    iconColor = "text-[#C62828]";
    barColor = "bg-[#EF5350]";
    textColor = "text-[#C62828]";
  }
  return { score, tone, label, reasons, iconBg, iconColor, barColor, textColor };
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddPet, setShowAddPet] = useState(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  const [showUpgradeNudge, setShowUpgradeNudge] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, rRes, mRes, tRes, sRes] = await Promise.all([
        fetch(`${API}/pets`, { credentials: "include" }),
        fetch(`${API}/reminders`, { credentials: "include" }),
        fetch(`${API}/milestones/today`, { credentials: "include" }),
        fetch(`${API}/timeline?limit=100`, { credentials: "include" }),
        fetch(`${API}/streak`, { credentials: "include" }),
      ]);
      if (pRes.ok) setPets(await pRes.json());
      if (rRes.ok) setReminders(await rRes.json());
      if (mRes.ok) {
        const data = await mRes.json();
        setMilestones(data.milestones || []);
      }
      if (tRes.ok) setTimeline(await tRes.json());
      if (sRes.ok) setStreak(await sRes.json());
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module-level const; setters are stable

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeletePet = async (petId) => {
    if (!window.confirm("Delete this pet and all their reminders?")) return;
    await fetch(`${API}/pets/${petId}`, { method: "DELETE", credentials: "include" });
    fetchData();
  };

  const handleDeleteReminder = async (reminderId) => {
    await fetch(`${API}/reminders/${reminderId}`, { method: "DELETE", credentials: "include" });
    setReminders(prev => prev.filter(r => r.reminder_id !== reminderId));
  };

  const handleCompleteReminder = async (reminderId) => {
    await fetch(`${API}/reminders/${reminderId}/complete`, { method: "POST", credentials: "include" });
    fetchData();
  };

  const activeReminders = reminders.filter(r => !r.completed);
  const overdueCount = activeReminders.filter(r => getDateStatus(r.due_date).isOverdue).length;
  const upcomingCount = activeReminders.filter(r => !getDateStatus(r.due_date).isOverdue).length;

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "#FAF9F6" }}>
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-12 flex flex-col gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-32 rounded-3xl shimmer-bg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }}>
      <Navigation />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Welcome header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 animate-fade-in-up">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800 font-['Nunito']">
              Hi, {user?.name?.split(" ")[0]} 👋
            </h1>
            <p className="text-slate-500 mt-1">Here's what's happening with your pets today</p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="add-pet-btn"
              onClick={() => {
                // Free-tier guard: convert the would-be 403 into a friendly upsell.
                if (user?.plan === "free" && pets.length >= 1) {
                  setShowUpgradeNudge(true);
                  return;
                }
                setEditingPet(null);
                setShowAddPet(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors shadow-sm"
            >
              <Plus size={16} /> Add Pet
            </button>
          </div>
        </div>

        {/* Free-plan upgrade nudge — shown when a free user tries to add a 2nd pet */}
        {showUpgradeNudge && (
          <div
            data-testid="upgrade-nudge"
            className="mb-6 rounded-2xl border-2 border-[#FF8A65] bg-[#FFF5E6] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-in-up"
            role="alert"
          >
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <PawPrint size={18} className="text-[#FF8A65]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-800 font-['Nunito'] text-sm">Free plan supports 1 pet</p>
              <p className="text-sm text-slate-600 mt-0.5">
                Upgrade to Premium for <strong>unlimited pets</strong>, AI Travel Mode, and emergency sharing.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                data-testid="upgrade-nudge-cta"
                onClick={() => navigate("/settings")}
                className="px-4 py-2.5 rounded-full bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors shadow-sm"
              >
                Upgrade
              </button>
              <button
                data-testid="upgrade-nudge-dismiss"
                onClick={() => setShowUpgradeNudge(false)}
                className="px-3 py-2.5 rounded-full text-slate-500 text-sm font-semibold hover:bg-white/60 transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        )}

        {/* Stats row */}
        {pets.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {/* Health streak — habit-loop card. Gentle empty-state nudge,
                celebratory active-state. Sits inline with the rest of the
                stats so it never feels bolted on. */}
            <div
              data-testid="streak-card"
              className={`rounded-2xl border shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-4 flex items-center gap-3 animate-fade-in-up ${
                streak?.is_active
                  ? "bg-gradient-to-br from-[#FFF5E6] to-[#FFE0B2] border-[#FFB74D]"
                  : "bg-white border-slate-100"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg ${
                  streak?.is_active ? "bg-white/70" : "bg-slate-50"
                }`}
                aria-hidden="true"
              >
                <span className={streak?.is_active ? "" : "grayscale opacity-40"}>🔥</span>
              </div>
              <div className="min-w-0">
                {streak?.is_active ? (
                  <>
                    <div data-testid="streak-weeks" className="text-xl font-black text-[#E65100] font-['Nunito']">
                      {streak.streak_weeks}
                    </div>
                    <div className="text-xs text-[#E65100]/80 font-semibold">
                      {streak.streak_weeks === 1 ? t("dashboard.streakWeeksSingular") : t("dashboard.streakWeeksPlural")}
                    </div>
                  </>
                ) : (
                  <>
                    <div data-testid="streak-empty" className="text-sm font-bold text-slate-700 font-['Nunito']">
                      {t("dashboard.streakStart")}
                    </div>
                    <div className="text-xs text-slate-400">{t("dashboard.streakStartHint")}</div>
                  </>
                )}
              </div>
            </div>

            {[
              { label: "Pets", value: pets.length, icon: PawPrint, color: "text-[#FF8A65]", bg: "bg-[#FFF5E6]" },
              { label: "Overdue", value: overdueCount, icon: AlertTriangle, color: overdueCount > 0 ? "text-[#EF5350]" : "text-slate-400", bg: overdueCount > 0 ? "bg-[#FFEBEE]" : "bg-slate-50" },
              { label: "Upcoming", value: upcomingCount, icon: Calendar, color: "text-[#66BB6A]", bg: "bg-[#E8F5E9]" },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-4 flex items-center gap-3 animate-fade-in-up">
                <div className={`w-10 h-10 rounded-full ${s.bg} flex items-center justify-center flex-shrink-0`}>
                  <s.icon size={18} className={s.color} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xl font-black text-slate-800 font-['Nunito']">{s.value}</div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Milestones (birthdays + tracking anniversaries) ──────────── */}
        {milestones.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="milestones-banner">
            {milestones.map((m, i) => {
              const isBirthday = m.type === "birthday";
              return (
                <div
                  key={`${m.pet_id}-${m.type}-${i}`}
                  data-testid={`milestone-${m.type}-${m.pet_id}`}
                  className={`relative overflow-hidden rounded-3xl border p-5 sm:p-6 flex items-center gap-4 ${
                    isBirthday
                      ? "bg-gradient-to-r from-[#FFF5E6] to-[#FFE0B2] border-[#FFB74D]"
                      : "bg-gradient-to-r from-[#E3F2FD] to-[#E8EAF6] border-[#90CAF9]"
                  }`}
                >
                  {/* Decorative emoji confetti */}
                  {isBirthday && (
                    <span className="absolute top-3 right-4 text-2xl" aria-hidden>🎉</span>
                  )}
                  {m.photo_url ? (
                    <img
                      src={m.photo_url}
                      alt={m.pet_name}
                      className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0 border-4 shadow-md ${
                        isBirthday ? "border-[#FFB74D]" : "border-[#90CAF9]"
                      }`}
                    />
                  ) : (
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isBirthday ? "bg-[#FFB74D]" : "bg-[#90CAF9]"
                    }`}>
                      {isBirthday
                        ? <Cake size={26} className="text-white" strokeWidth={2.5} />
                        : <Gift size={26} className="text-white" strokeWidth={2.5} />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-extrabold text-base sm:text-lg font-['Nunito'] ${
                      isBirthday ? "text-[#E65100]" : "text-[#1565C0]"
                    }`}>
                      {m.headline}
                    </p>
                    <p className="text-sm text-slate-600 mt-0.5">{m.message}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/passport/${m.pet_id}`)}
                    data-testid={`milestone-cta-${m.pet_id}`}
                    className={`hidden sm:inline-flex flex-shrink-0 items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold shadow-sm transition-all ${
                      isBirthday
                        ? "bg-[#FF8A65] hover:bg-[#FF7043] text-white"
                        : "bg-[#1565C0] hover:bg-[#0D47A1] text-white"
                    }`}
                  >
                    Open passport →
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Weekly Wellness Snapshot ───────────────────────────────── */}
        {pets.length > 0 && (
          <div className="mb-8" data-testid="wellness-snapshot">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={15} className="text-[#FF8A65]" />
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider">{t("dashboard.wellnessSnapshot")}</h2>
              <span className="text-xs text-slate-400 ml-auto">Live · updates as you add data</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pets.map((pet) => {
                const w = computeWellness(pet, reminders);
                return (
                  <button
                    key={pet.pet_id}
                    data-testid={`wellness-card-${pet.pet_id}`}
                    onClick={() => navigate(`/passport/${pet.pet_id}`)}
                    className="text-left bg-white rounded-3xl border border-slate-100 shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${w.iconBg}`}>
                        <Heart size={18} className={w.iconColor} strokeWidth={2.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-800 font-['Nunito'] truncate">{pet.name}</p>
                        <p className={`text-xs font-semibold ${w.textColor}`} data-testid={`wellness-label-${pet.pet_id}`}>
                          {w.label}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-2xl font-black font-['Nunito'] tabular-nums ${w.textColor}`} data-testid={`wellness-score-${pet.pet_id}`}>
                          {w.score}
                        </p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider -mt-1">/ 100</p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-3">
                      <div
                        className={`h-full ${w.barColor} transition-all duration-500`}
                        style={{ width: `${w.score}%` }}
                      />
                    </div>
                    {/* Top reasons — up to 3, sorted by severity */}
                    {w.reasons.length > 0 ? (
                      <ul className="space-y-1">
                        {w.reasons
                          .slice()
                          .sort((a, b) => {
                            const order = { high: 0, medium: 1, low: 2 };
                            return order[a.severity] - order[b.severity];
                          })
                          .slice(0, 3)
                          .map((r, i) => {
                            const dotColor =
                              r.severity === "high" ? "bg-[#EF5350]" :
                              r.severity === "medium" ? "bg-[#FFB74D]" :
                              "bg-slate-300";
                            return (
                              <li key={`${r.text}-${r.severity}`} className="flex items-start gap-2 text-xs text-slate-600">
                                <span className={`w-1.5 h-1.5 rounded-full ${dotColor} mt-1.5 flex-shrink-0`} />
                                <span className="leading-snug">{r.text}</span>
                              </li>
                            );
                          })}
                      </ul>
                    ) : (
                      <p className="text-xs text-[#388E3C] font-semibold flex items-center gap-1.5">
                        <Check size={13} strokeWidth={2.5} /> Everything looks great — nice work!
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* No pets state */}
        {pets.length === 0 && (
          <div className="text-center py-20 animate-fade-in">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#FFF5E6] flex items-center justify-center mb-6">
              <PawPrint size={36} className="text-[#FF8A65]" />
            </div>
            <h2 className="text-2xl font-extrabold text-slate-800 font-['Nunito'] mb-3">Add your first pet</h2>
            <p className="text-slate-500 mb-8 max-w-sm mx-auto">Start managing your pet's health, reminders, and records all in one place.</p>
            <button
              data-testid="add-first-pet-btn"
              onClick={() => setShowAddPet(true)}
              className="px-8 py-3.5 rounded-full bg-[#FF8A65] text-white font-semibold hover:bg-[#FF7043] transition-all shadow-lg hover:-translate-y-1"
            >
              Add Your Pet
            </button>
          </div>
        )}

        {/* Bento grid — pets + reminders */}
        {pets.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pets column */}
            <div className="lg:col-span-1 space-y-4">
              {pets.map((pet, i) => (
                <div
                  key={pet.pet_id}
                  className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 animate-fade-in-up"
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`pet-card-${pet.pet_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    {pet.photo_url ? (
                      <img src={pet.photo_url} alt={pet.name} className="w-16 h-16 rounded-2xl object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-[#FFF5E6] flex items-center justify-center">
                        <PawPrint size={28} className="text-[#FF8A65]" />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button data-testid={`edit-pet-${pet.pet_id}`} onClick={() => { setEditingPet(pet); setShowAddPet(true); }} className="p-2 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors">
                        <Edit2 size={15} />
                      </button>
                      <button data-testid={`delete-pet-${pet.pet_id}`} onClick={() => handleDeletePet(pet.pet_id)} className="p-2 rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 font-['Nunito']">{pet.name}</h3>
                  <p className="text-sm text-slate-500 mb-1">{SPECIES_LABELS[pet.species] || pet.species}{pet.breed ? ` · ${pet.breed}` : ""}</p>
                  {pet.date_of_birth && <p className="text-xs text-slate-400">{calculateAge(pet.date_of_birth)} old</p>}
                  {pet.weight && <p className="text-xs text-slate-400">{pet.weight} kg</p>}

                  {/* Vaccination status — surfaces overdue/due-soon at a glance */}
                  {Array.isArray(pet.vaccinations) && pet.vaccinations.filter(v => v?.name).length > 0 && (() => {
                    const decorated = pet.vaccinations
                      .filter(v => v?.name)
                      .map(v => ({ ...v, status: vaccStatus(v.next_due) }))
                      .sort((a, b) => a.status.sortRank - b.status.sortRank);
                    const visible = decorated.slice(0, 3);
                    const hidden = decorated.length - visible.length;
                    const overdueCount = decorated.filter(v => v.status.tone === "overdue").length;
                    const dueSoonCount = decorated.filter(v => v.status.tone === "due-soon" || v.status.tone === "due-3mo").length;
                    return (
                      <div
                        className="mt-3 pt-3 border-t border-slate-100"
                        data-testid={`vacc-status-${pet.pet_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Vaccinations</p>
                          {overdueCount > 0 ? (
                            <span className="text-[10px] font-bold uppercase bg-[#FFEBEE] text-[#C62828] px-2 py-0.5 rounded-full">
                              {overdueCount} overdue
                            </span>
                          ) : dueSoonCount > 0 ? (
                            <span className="text-[10px] font-bold uppercase bg-[#FFF3E0] text-[#E65100] px-2 py-0.5 rounded-full">
                              {dueSoonCount} due soon
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase bg-[#E8F5E9] text-[#388E3C] px-2 py-0.5 rounded-full">
                              All current
                            </span>
                          )}
                        </div>
                        <ul className="space-y-1">
                          {visible.map((v, i) => {
                            const Icon = v.status.Icon;
                            return (
                              <li
                                key={`${v.name}-${v.next_due || i}`}
                                className="flex items-center justify-between text-xs"
                                data-testid={`vacc-row-${pet.pet_id}-${i}`}
                              >
                                <span className="flex items-center gap-1.5 min-w-0 flex-1 truncate">
                                  <Icon size={13} className={v.status.iconClass} strokeWidth={2.5} />
                                  <span className="text-slate-700 font-medium truncate">{v.name}</span>
                                </span>
                                <span className={`text-[11px] ${v.status.labelClass} flex-shrink-0 ml-2`}>
                                  {v.status.label}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                        {hidden > 0 && (
                          <button
                            data-testid={`vacc-more-${pet.pet_id}`}
                            onClick={() => navigate(`/passport/${pet.pet_id}`)}
                            className="text-[11px] text-slate-400 hover:text-[#FF8A65] mt-1.5 transition-colors"
                          >
                            +{hidden} more →
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  <div className="flex gap-2 mt-4">
                    <button
                      data-testid={`passport-btn-${pet.pet_id}`}
                      onClick={() => navigate(`/passport/${pet.pet_id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#E8F5E9] text-[#388E3C] text-xs font-semibold hover:bg-[#C8E6C9] transition-colors"
                    >
                      <Shield size={13} /> Passport
                    </button>
                    <button
                      data-testid={`add-reminder-pet-${pet.pet_id}`}
                      onClick={() => { setEditingReminder({ pet_id: pet.pet_id }); setShowAddReminder(true); }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#FFF5E6] text-[#FF8A65] text-xs font-semibold hover:bg-[#FFDDB3] transition-colors"
                    >
                      <Plus size={13} /> Reminder
                    </button>
                  </div>
                </div>
              ))}

              {/* Quick actions */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-5">
                <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h4>
                <div className="space-y-2">
                  <button onClick={() => navigate("/travel")} className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#E3F2FD] text-[#1565C0] hover:bg-[#BBDEFB] transition-colors text-sm font-semibold" data-testid="go-travel-btn">
                    <Plane size={16} /> Travel Mode <span className="ml-auto text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">AI</span>
                  </button>
                  <button onClick={() => navigate("/emergency")} className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#FFEBEE] text-[#C62828] hover:bg-[#FFCDD2] transition-colors text-sm font-semibold" data-testid="go-emergency-btn">
                    <AlertTriangle size={16} /> Emergency Mode
                  </button>
                </div>
              </div>
            </div>

            {/* Reminders column */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-800 font-['Nunito']">{t("dashboard.upcomingReminders")}</h3>
                  <button
                    data-testid="add-reminder-btn"
                    onClick={() => { setEditingReminder(null); setShowAddReminder(true); }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors"
                  >
                    <Plus size={14} /> {t("dashboard.addReminder")}
                  </button>
                </div>

                {activeReminders.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar size={36} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 font-medium">{t("dashboard.noReminders")}</p>
                    <p className="text-sm text-slate-400 mt-1">{t("dashboard.noRemindersHint")}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeReminders.map((r, i) => {
                      const status = getDateStatus(r.due_date);
                      const colors = REMINDER_COLORS[r.type] || REMINDER_COLORS.custom;
                      const Icon = REMINDER_ICONS[r.type] || Calendar;
                      return (
                        <div
                          key={r.reminder_id}
                          data-testid={`reminder-${r.reminder_id}`}
                          className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-sm animate-fade-in-up ${
                            status.isOverdue
                              ? "border-red-200 bg-[#FFEBEE]"
                              : status.isToday
                              ? "border-orange-200 bg-[#FFF3E0]"
                              : "border-slate-100 bg-slate-50/50"
                          }`}
                          style={{ animationDelay: `${i * 0.03}s` }}
                        >
                          <div
                            className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                            style={{ background: `${colors.dot}25` }}
                          >
                            <Icon size={18} style={{ color: colors.dot }} strokeWidth={2.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-slate-800 truncate">{r.title}</p>
                              {r.recurring && <Clock size={12} className="text-slate-400 flex-shrink-0" title="Recurring" />}
                            </div>
                            <p className="text-xs text-slate-500">{r.pet_name} · {formatDate(r.due_date)}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                              status.isOverdue ? "bg-red-100 text-red-600" :
                              status.isToday ? "bg-orange-100 text-orange-600" :
                              "bg-slate-100 text-slate-600"
                            }`}>{status.label}</span>
                            <button
                              data-testid={`complete-reminder-${r.reminder_id}`}
                              onClick={() => handleCompleteReminder(r.reminder_id)}
                              title="Mark complete"
                              className="p-1.5 rounded-lg hover:bg-green-50 text-slate-300 hover:text-green-500 transition-colors"
                            >
                              <CheckCircle2 size={18} />
                            </button>
                            <button
                              data-testid={`snooze-reminder-${r.reminder_id}`}
                              onClick={() => handleSnoozeReminder(r.reminder_id)}
                              title="Snooze 3 days"
                              className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-300 hover:text-amber-500 transition-colors"
                            >
                              <AlarmClock size={16} />
                            </button>
                            <button
                              data-testid={`delete-reminder-${r.reminder_id}`}
                              onClick={() => handleDeleteReminder(r.reminder_id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-400 transition-colors"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Care Timeline ─────────────────────────────────────────────── */}
        {!loading && pets.length > 0 && (
          <section className="mt-8" data-testid="care-timeline-section">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 md:p-8 animate-fade-in-up">
              <div className="flex items-start gap-3 mb-6">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FFE0CC] to-[#FFC9A8] flex items-center justify-center flex-shrink-0">
                  <History size={20} className="text-[#FF8A65]" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-800 font-['Nunito']">{t("dashboard.careTimeline")}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t("dashboard.careTimelineSub")}
                  </p>
                </div>
                {timeline.length > 0 && (
                  <span data-testid="timeline-count" className="text-[11px] font-semibold text-slate-400 bg-slate-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {t("dashboard.entries", { count: timeline.length })}
                  </span>
                )}
              </div>

              {timeline.length === 0 ? (
                <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-testid="timeline-empty">
                  <History size={28} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 font-medium">{t("dashboard.careTimelineEmpty")}</p>
                  <p className="text-xs text-slate-400 mt-1">{t("dashboard.careTimelineEmptyHint")}</p>
                </div>
              ) : (
                <div className="relative" data-testid="timeline-list">
                  {/* vertical track */}
                  <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-[#FFD7C2] via-slate-100 to-transparent" aria-hidden="true" />
                  {(() => {
                    let lastGroup = null;
                    return timeline.map((e, i) => {
                      const meta = TIMELINE_KIND[e.kind] || TIMELINE_KIND.custom;
                      const Icon = meta.Icon;
                      const group = timelineGroupKey(e.date);
                      const showHeader = group !== lastGroup;
                      lastGroup = group;
                      return (
                        <React.Fragment key={e.event_id}>
                          {showHeader && (
                            <div
                              className="relative pl-12 pt-3 pb-2 first:pt-0"
                              data-testid={`timeline-group-${group}`}
                            >
                              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                                {group}
                              </p>
                            </div>
                          )}
                          <div
                            data-testid={`timeline-event-${e.event_id}`}
                            className="relative pl-12 pb-4 last:pb-0 animate-fade-in-up"
                            style={{ animationDelay: `${Math.min(i, 12) * 0.025}s` }}
                          >
                            <div
                              className={`absolute left-0 top-0 w-10 h-10 rounded-2xl ${meta.bg} flex items-center justify-center ring-4 ring-white`}
                            >
                              <Icon size={16} className={meta.color} strokeWidth={2.5} />
                            </div>
                            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-800 truncate">
                                  {e.title}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                                  <span className={`font-semibold ${meta.color}`}>{t(meta.labelKey)}</span>
                                  {e.pet_name && (
                                    <>
                                      <span className="text-slate-300">·</span>
                                      <span>{e.pet_name}</span>
                                    </>
                                  )}
                                </div>
                                {e.notes && (
                                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{e.notes}</p>
                                )}
                              </div>
                              <span className="text-[11px] text-slate-400 font-medium whitespace-nowrap">
                                {formatDate(e.date)}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <AddPetModal
        open={showAddPet}
        onClose={() => { setShowAddPet(false); setEditingPet(null); }}
        onSaved={fetchData}
        editPet={editingPet}
        userPlan={user?.plan}
        petCount={pets.length}
      />
      <AddReminderModal
        open={showAddReminder}
        onClose={() => { setShowAddReminder(false); setEditingReminder(null); }}
        onSaved={fetchData}
        pets={pets}
        defaultPetId={editingReminder?.pet_id}
      />
    </div>
  );
}
